import { type Availability, type Court, type Extension, MatchUpTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

import { getClientConfig } from 'config/clients';
import { getCourtToAssignMatch } from 'containers/schedule/utils';
import { type CourtType, type MatchUpInterface } from 'typedefs/store/scheduleTypes';
import { factorySafeTimeToDayjs } from 'utils/dateTimeUtils';
import { areIntervalsOverlapping } from 'utils/math';

const courtAvailableHours = getClientConfig().courtsAvailableHours ?? 15;
const courtsDefaultMaxStartTime = getClientConfig().courtsDefaultMaxStartTime ?? '22:00';

export const INITIAL_COURT_AVAILABILITY: Availability = {
  startTime: '08:00',
  endTime: '20:00',
};

export interface DateOption {
  text: string;
  value: string;
}

export enum AvailabilityType {
  'START',
  'END',
}

export const getRowId = (row: Court) => row.courtId;

export const getCurrentDate = () => {
  return dayjs().format('YYYY-MM-DD');
};

export const getCourtAvailabilitiesByDate = (courts: Court[], selectedDate: string, localTimeZone: string) =>
  courts.reduce((availabilities: Availability[], currentCourt: Court) => {
    const dayAvailabilities = currentCourt.dateAvailability.filter(
      (availability) =>
        factorySafeTimeToDayjs(availability.date, localTimeZone)?.toISOString() ===
        factorySafeTimeToDayjs(selectedDate, localTimeZone)?.toISOString(),
    );

    const newAvailabilities = dayAvailabilities.filter(
      (availability) =>
        !availabilities.some((a) => a.startTime === availability.startTime && a.endTime === availability.endTime),
    );

    return [...availabilities, ...newAvailabilities];
  }, []);

export const checkFreeCourtPeriods = (courtAvailablilties: Availability[]) => {
  const usedHours = courtAvailablilties.reduce((workingHours, availability) => {
    const from = parseInt(availability.startTime);
    const to = parseInt(availability.endTime);

    if (!Number.isNaN(from) && !Number.isNaN(to)) {
      return workingHours + (to - from);
    }
    return workingHours;
  }, 0);

  return usedHours < courtAvailableHours;
};

export const isTimeOverlaping = (courtAvailabilities: Availability[]) =>
  courtAvailabilities.some((a, i) =>
    courtAvailabilities.some((b, j) => {
      const a_startTime = Number.parseFloat(a.startTime.replace(':', '.'));
      const a_endTime = Number.parseFloat(a.endTime.replace(':', '.'));
      const b_startTime = Number.parseFloat(b.startTime.replace(':', '.'));
      const b_endTime = Number.parseFloat(b.endTime.replace(':', '.'));

      return i !== j && areIntervalsOverlapping([a_startTime, a_endTime], [b_startTime, b_endTime]);
    }),
  );

export const checkCourtAvailability = (startTime: string, endTime: string) => {
  const startDate = dayjs(`${getCurrentDate()} ${startTime}`);
  const endDate = dayjs(`${getCurrentDate()} ${endTime}`);
  if (startDate.isAfter(endDate)) return 'Start time cannot be after end time';
  const duration = dayjs.duration(endDate.diff(startDate));
  const minutes = duration.minutes();
  const hours = duration.hours();
  if (minutes <= 30 && hours === 0) return 'Difference between start and end time must be greater than 30';
  return '';
};

export const getAddNewTime = (courtAvailabilities: Availability[]) => {
  const prevAvailibility = courtAvailabilities?.length && courtAvailabilities[courtAvailabilities.length - 1];

  const courtDefaultStartTime = INITIAL_COURT_AVAILABILITY.startTime;
  const courtDefaultEndTime = INITIAL_COURT_AVAILABILITY.endTime;

  const [defaultStartTime, defaultEndTime, maxStartTime] = [
    courtDefaultStartTime,
    courtDefaultEndTime,
    courtsDefaultMaxStartTime,
  ].map((time) => dayjs(`${getCurrentDate()} ${time}`));

  const prevStartTime = prevAvailibility?.endTime && dayjs(`${getCurrentDate()} ${prevAvailibility.endTime}`);

  let startTimeDate = maxStartTime;
  if (!prevStartTime) startTimeDate = defaultStartTime;
  else if (prevStartTime.isSameOrBefore(maxStartTime)) startTimeDate = prevStartTime;

  const endTimeDate = startTimeDate.isSameOrBefore(maxStartTime) ? startTimeDate.add(1, 'hour') : defaultEndTime;

  return { startTimeDate: startTimeDate.format('HH:mm'), endTimeDate: endTimeDate.format('HH:mm') };
};

export const getDates = (startDate: string, endDate: string) => {
  const dates: { id: string; day: Date }[] = [];

  let startTime = '';
  if (startDate?.length === 10) startTime = '00:00:00';
  else if (startDate?.length > 10) startTime = startDate?.split('T')[1].replace(/z/gi, '');

  let endTime = '';
  if (endDate?.length === 10) endTime = '00:00:00';
  else if (endDate?.length > 10) endTime = endDate?.split('T')[1].replace(/z/gi, '');

  const startMomentTime = startDate && dayjs(startDate.split('T')[0] + 'T' + startTime);
  const endMomentTime = endDate && dayjs(endDate.split('T')[0] + 'T' + endTime);

  if (endMomentTime?.isBefore(startMomentTime)) return [];

  const startIsValid = startMomentTime?.isValid();
  const endIsValid = endMomentTime?.isValid();

  const sDate = startIsValid && startMomentTime?.format('YYYY-MM-DDTHH:mm:ss');
  const eDate = endIsValid && endMomentTime?.format('YYYY-MM-DDTHH:mm:ss');

  let currDate = dayjs(sDate).startOf('day');
  const earliestDate = dayjs(sDate).startOf('day');
  const lastDate = dayjs(eDate).startOf('day');

  while (currDate.diff(lastDate) < 0) {
    dates.push({ id: uuidv4(), day: currDate.toDate() });
    currDate = currDate.add(1, 'day');
  }
  if (earliestDate.isSame(lastDate) === false) {
    dates.push({ id: uuidv4(), day: dayjs(eDate).toDate() });
  }
  return dates;
};

export const findCourtNameLastIndex = (courtName: string, courts: Court[]) => {
  const existingCourtsWithCourtsName =
    courts?.filter((court) => {
      const splittedName = court.courtName?.split(' ');
      const nameWithoutIndex = splittedName.slice(0, splittedName.length - 1).join(' ');
      return nameWithoutIndex === courtName;
    }) ?? [];

  return (
    existingCourtsWithCourtsName[existingCourtsWithCourtsName.length - 1]?.courtName?.split(' ')?.splice(-1)?.[0] ?? '0'
  );
};

export const isCourtDisabled = (courtExtensions: Extension[], date: string) => {
  const disabledDates = courtExtensions?.find((extension) => extension.name === 'disabled')?.value?.dates;
  if (!disabledDates) return false;

  return disabledDates?.indexOf(date) !== -1;
};

export const getAvailableCourts = (courts: any[], allocatedCourts: { courtId: string }[], selectedDate: string) =>
  courts.filter(
    (court) =>
      !isCourtDisabled(court.extensions, selectedDate) &&
      (court?.matchUps?.length === 0 || allocatedCourts?.some(({ courtId }) => court?.courtId === courtId)),
  );

export const mapAssignData = (
  matchUp: MatchUpInterface,
  court?: CourtType,
  teamCourts?: CourtType[],
  courtsData?: any[],
) => {
  const isTeamMatch = matchUp?.matchUpType === MatchUpTypeEnum.TEAM;
  const courtToAssign = getCourtToAssignMatch(matchUp, court, undefined, courtsData);
  const teamCourtsToAssign = getCourtToAssignMatch(matchUp, undefined, teamCourts, courtsData);

  return {
    matchUpToAssign: {
      ...matchUp,
      schedule: {
        ...matchUp.schedule,
        courtId: !isTeamMatch ? courtToAssign?.courtId : undefined,
        courtIds: isTeamMatch && teamCourtsToAssign?.length ? teamCourtsToAssign?.map((c) => c.courtId) : undefined,
      },
    },
    courtToAssign,
    teamCourtsToAssign,
  };
};

export const displayCourtAvailabilityTime = (time: string, localTimeZone: string) =>
  factorySafeTimeToDayjs(`${getCurrentDate()} ${time}`, localTimeZone).format('h:mm A').toLowerCase();
